import { Controller } from "@hotwired/stimulus"

// 전역 오디오 컨텍스트 (페이지 이동 시에도 유지)
let globalAudioContext: AudioContext | null = null
let globalBgmGain: GainNode | null = null
let globalBgmTimeout: any = null
let globalBgmStarted = false
let globalBeatCount = 0

export default class extends Controller<HTMLElement> {
  static targets = ["candidate"]

  declare readonly candidateTargets: HTMLElement[]

  connect(): void {
    console.log("WorldCupSoundController connected!")

    // 전역 오디오 컨텍스트 사용
    if (!globalAudioContext) {
      globalAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
    }

    // BGM이 시작되지 않았다면 첫 클릭 시 시작
    if (!globalBgmStarted) {
      this.startBgmOnInteraction = this.startBgmOnInteraction.bind(this)
      document.addEventListener("click", this.startBgmOnInteraction, { once: true })
      document.addEventListener("touchstart", this.startBgmOnInteraction, { once: true })
    }
  }

  disconnect(): void {
    // 페이지 이동 시에도 BGM은 계속 재생 (disconnect에서 stopBgm 호출 안함)
    // 게임 종료 시에만 stopBgm 호출
  }

  getAudioContext(): AudioContext {
    if (!globalAudioContext) {
      globalAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
    }
    // suspended 상태면 resume
    if (globalAudioContext.state === 'suspended') {
      globalAudioContext.resume()
    }
    return globalAudioContext
  }

  startBgmOnInteraction(): void {
    console.log("Starting BGM on interaction...")
    if (!globalBgmStarted) {
      this.startBgm()
      globalBgmStarted = true
      console.log("BGM started!")
    }
  }

  // 두근두근 긴장감 있는 배경음악
  startBgm(): void {
    const ctx = this.getAudioContext()

    // 마스터 게인
    globalBgmGain = ctx.createGain()
    globalBgmGain.gain.setValueAtTime(0.2, ctx.currentTime)
    globalBgmGain.connect(ctx.destination)

    // 심장박동 시작
    this.playHeartbeatLoop(ctx)
  }

  playHeartbeatLoop(ctx: AudioContext): void {
    if (!globalBgmGain) return

    const now = ctx.currentTime
    const bpm = 120 // 긴장감 있는 빠른 템포
    const beatDuration = 60 / bpm

    // 심장박동 패턴: 쿵-쿵 (두 번 연속)
    this.createHeartbeat(ctx, now, 55, 0.4)
    this.createHeartbeat(ctx, now + 0.12, 50, 0.3)

    // 서브베이스
    this.createSubBass(ctx, now, beatDuration)

    // 4비트마다 긴장감 신스
    if (globalBeatCount % 4 === 0) {
      this.createTensionSynth(ctx, now + beatDuration * 0.5)
    }

    // 8비트마다 라이저
    if (globalBeatCount % 8 === 0) {
      this.createRiser(ctx, now, beatDuration * 2)
    }

    globalBeatCount++

    // 다음 비트 스케줄
    globalBgmTimeout = setTimeout(() => {
      this.playHeartbeatLoop(ctx)
    }, beatDuration * 1000)
  }

  // 심장박동 사운드
  createHeartbeat(ctx: AudioContext, time: number, freq: number, volume: number): void {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.type = "sine"
    osc.frequency.setValueAtTime(freq, time)
    osc.frequency.exponentialRampToValueAtTime(25, time + 0.15)

    gain.gain.setValueAtTime(0, time)
    gain.gain.linearRampToValueAtTime(volume, time + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.01, time + 0.18)

    osc.connect(gain)
    if (globalBgmGain) gain.connect(globalBgmGain)

    osc.start(time)
    osc.stop(time + 0.18)
  }

  // 서브베이스 드론
  createSubBass(ctx: AudioContext, time: number, duration: number): void {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.type = "sine"
    osc.frequency.setValueAtTime(40, time)

    gain.gain.setValueAtTime(0.1, time)
    gain.gain.setValueAtTime(0.1, time + duration * 0.8)
    gain.gain.linearRampToValueAtTime(0.03, time + duration)

    osc.connect(gain)
    if (globalBgmGain) gain.connect(globalBgmGain)

    osc.start(time)
    osc.stop(time + duration)
  }

  // 긴장감 신스
  createTensionSynth(ctx: AudioContext, time: number): void {
    const frequencies = [220, 277, 330]

    frequencies.forEach((freq) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      const filter = ctx.createBiquadFilter()

      osc.type = "sawtooth"
      osc.frequency.setValueAtTime(freq, time)

      filter.type = "lowpass"
      filter.frequency.setValueAtTime(600, time)

      gain.gain.setValueAtTime(0, time)
      gain.gain.linearRampToValueAtTime(0.025, time + 0.05)
      gain.gain.exponentialRampToValueAtTime(0.01, time + 0.25)

      osc.connect(filter)
      filter.connect(gain)
      if (globalBgmGain) gain.connect(globalBgmGain)

      osc.start(time)
      osc.stop(time + 0.25)
    })
  }

  // 라이저 효과
  createRiser(ctx: AudioContext, time: number, duration: number): void {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    const filter = ctx.createBiquadFilter()

    osc.type = "sawtooth"
    osc.frequency.setValueAtTime(80, time)
    osc.frequency.exponentialRampToValueAtTime(300, time + duration)

    filter.type = "lowpass"
    filter.frequency.setValueAtTime(150, time)
    filter.frequency.exponentialRampToValueAtTime(1200, time + duration)

    gain.gain.setValueAtTime(0, time)
    gain.gain.linearRampToValueAtTime(0.03, time + duration * 0.5)
    gain.gain.linearRampToValueAtTime(0, time + duration)

    osc.connect(filter)
    filter.connect(gain)
    if (globalBgmGain) gain.connect(globalBgmGain)

    osc.start(time)
    osc.stop(time + duration)
  }

  // BGM 정지 (게임 완전 종료 시에만 호출)
  static stopBgm(): void {
    if (globalBgmTimeout) {
      clearTimeout(globalBgmTimeout)
      globalBgmTimeout = null
    }
    if (globalBgmGain) {
      globalBgmGain.disconnect()
      globalBgmGain = null
    }
    globalBgmStarted = false
    globalBeatCount = 0
  }

  // 선택 효과음
  playSelectSound(): void {
    const ctx = this.getAudioContext()
    const sounds = [
      () => this.playPopSound(ctx),
      () => this.playWhooshSound(ctx),
      () => this.playChimeSound(ctx),
      () => this.playBlingSound(ctx),
      () => this.playPunchSound(ctx)
    ]

    const randomSound = sounds[Math.floor(Math.random() * sounds.length)]
    randomSound()
  }

  // 팝 사운드
  playPopSound(ctx: AudioContext): void {
    const now = ctx.currentTime

    const osc = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.type = "sine"
    osc.frequency.setValueAtTime(400, now)
    osc.frequency.exponentialRampToValueAtTime(800, now + 0.1)

    gain.gain.setValueAtTime(0.5, now)
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.15)

    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.start(now)
    osc.stop(now + 0.15)

    // 하모닉
    const osc2 = ctx.createOscillator()
    const gain2 = ctx.createGain()
    osc2.type = "triangle"
    osc2.frequency.setValueAtTime(800, now)
    osc2.frequency.exponentialRampToValueAtTime(1200, now + 0.08)
    gain2.gain.setValueAtTime(0.25, now)
    gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.1)
    osc2.connect(gain2)
    gain2.connect(ctx.destination)
    osc2.start(now)
    osc2.stop(now + 0.1)
  }

  // 휙~ 사운드
  playWhooshSound(ctx: AudioContext): void {
    const now = ctx.currentTime

    const bufferSize = ctx.sampleRate * 0.2
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate)
    const data = buffer.getChannelData(0)

    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize)
    }

    const noise = ctx.createBufferSource()
    noise.buffer = buffer

    const filter = ctx.createBiquadFilter()
    filter.type = "bandpass"
    filter.frequency.setValueAtTime(1000, now)
    filter.frequency.exponentialRampToValueAtTime(3000, now + 0.1)
    filter.Q.value = 1

    const gain = ctx.createGain()
    gain.gain.setValueAtTime(0.4, now)
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2)

    noise.connect(filter)
    filter.connect(gain)
    gain.connect(ctx.destination)
    noise.start(now)
  }

  // 차임 사운드
  playChimeSound(ctx: AudioContext): void {
    const now = ctx.currentTime
    const frequencies = [523, 659, 784]

    frequencies.forEach((freq, i) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()

      osc.type = "sine"
      osc.frequency.setValueAtTime(freq, now + i * 0.03)

      gain.gain.setValueAtTime(0.3, now + i * 0.03)
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.3)

      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.start(now + i * 0.03)
      osc.stop(now + 0.3)
    })
  }

  // 블링 사운드
  playBlingSound(ctx: AudioContext): void {
    const now = ctx.currentTime
    const notes = [600, 800, 1000, 1200]

    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()

      osc.type = "sine"
      osc.frequency.setValueAtTime(freq, now + i * 0.04)

      gain.gain.setValueAtTime(0.25, now + i * 0.04)
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2 + i * 0.04)

      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.start(now + i * 0.04)
      osc.stop(now + 0.2 + i * 0.04)
    })
  }

  // 펀치 사운드
  playPunchSound(ctx: AudioContext): void {
    const now = ctx.currentTime

    const osc1 = ctx.createOscillator()
    const gain1 = ctx.createGain()
    osc1.type = "sine"
    osc1.frequency.setValueAtTime(150, now)
    osc1.frequency.exponentialRampToValueAtTime(50, now + 0.1)
    gain1.gain.setValueAtTime(0.6, now)
    gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.15)
    osc1.connect(gain1)
    gain1.connect(ctx.destination)
    osc1.start(now)
    osc1.stop(now + 0.15)

    const osc2 = ctx.createOscillator()
    const gain2 = ctx.createGain()
    osc2.type = "square"
    osc2.frequency.setValueAtTime(1000, now)
    gain2.gain.setValueAtTime(0.2, now)
    gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.03)
    osc2.connect(gain2)
    gain2.connect(ctx.destination)
    osc2.start(now)
    osc2.stop(now + 0.03)
  }

  select(event: Event): void {
    console.log("Select triggered!")
    // 효과음 재생
    this.playSelectSound()

    // 선택된 카드에 애니메이션 추가
    const candidate = event.currentTarget as HTMLElement
    candidate.classList.add("scale-105", "z-40")

    // 폼 제출
    const robotId = candidate.dataset.robotId
    console.log("Submitting form for robot:", robotId)
    setTimeout(() => {
      const form = document.getElementById(`form_${robotId}`) as HTMLFormElement
      if (form) form.submit()
    }, 150)
  }
}

// 게임 페이지를 떠날 때 BGM 정지
document.addEventListener("turbo:before-visit", (event: Event) => {
  // 월드컵 게임 페이지가 아닌 곳으로 이동할 때만 BGM 정지
  const customEvent = event as CustomEvent
  const targetUrl = customEvent.detail.url
  if (!targetUrl.includes("/world_cup_games/") || targetUrl.includes("/result") || targetUrl.includes("/rankings")) {
    if (globalBgmTimeout) {
      clearTimeout(globalBgmTimeout)
      globalBgmTimeout = null
    }
    if (globalBgmGain) {
      globalBgmGain.disconnect()
      globalBgmGain = null
    }
    globalBgmStarted = false
    globalBeatCount = 0
    console.log("BGM stopped - leaving game")
  }
})
